import { Component, computed, HostListener, inject, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { NgClass } from '@angular/common';
import { Subscription } from 'rxjs';
import { SidebarComponent } from './sidebar/sidebar.component';
import { TopbarComponent } from './topbar/topbar.component';
import { IdleTimeoutComponent } from '../shared/components/idle-timeout/idle-timeout.component';
import { LayoutService } from './layout.service';
import { AuthService } from '../core/auth/auth.service';
import { NotificationService } from '../core/services/notification.service';

const SLOW_TYPES = new Set(['slow-2g', '2g']);

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [RouterOutlet, NgClass, SidebarComponent, TopbarComponent, IdleTimeoutComponent],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.css']
})
export class LayoutComponent implements OnInit, OnDestroy {
  layoutService  = inject(LayoutService);
  private auth   = inject(AuthService);
  private notify = inject(NotificationService);
  private platformId = inject(PLATFORM_ID);

  year = new Date().getFullYear();
  isOffline      = signal(false);
  isSlowNetwork  = signal(false);
  private subs   = new Subscription();
  private connectionListener: (() => void) | null = null;

  readonly isProviderUser: boolean = (() => {
    const user    = this.auth.getCurrentUser();
    const modules = user?.modules ?? [];
    return (
      user?.userType === 'Provider' &&
      modules.length > 0 &&
      !modules.includes('*') &&
      modules.some(m => m.toLowerCase() === 'provider')
    );
  })();

  containerClass = computed(() => {
    const mode  = this.layoutService.menuMode();
    const state = this.layoutService.state();
    return {
      'layout-static':           !this.isProviderUser && mode === 'static',
      'layout-overlay':          !this.isProviderUser && mode === 'overlay',
      'layout-slim':             !this.isProviderUser && mode === 'slim',
      'layout-slim-plus':        !this.isProviderUser && mode === 'slim-plus',
      'layout-static-inactive':  !this.isProviderUser && mode === 'static'  && state.staticMenuDesktopInactive,
      'layout-overlay-active':   !this.isProviderUser && state.overlayMenuActive,
      'layout-sidebar-active':   !this.isProviderUser && state.sidebarActive,
      'layout-anchored':         !this.isProviderUser && state.anchored,
      'layout-no-sidebar':       this.isProviderUser,
    };
  });

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isOffline.set(!navigator.onLine);
      this.checkConnectionSpeed();

      // Listen for Network Information API changes (Chrome/Edge/Android)
      const conn = (navigator as any).connection;
      if (conn) {
        this.connectionListener = () => this.checkConnectionSpeed();
        conn.addEventListener('change', this.connectionListener);
      }
    }

    this.subs.add(
      this.auth.sessionExpiring$.subscribe(secondsLeft => {
        this.notify.warn(
          'Session Expiring',
          `Your session will expire in ${secondsLeft} seconds. Please save your work.`
        );
      })
    );

    this.subs.add(
      this.auth.sessionEnded$.subscribe(reason => {
        if (reason === 'expired') {
          // Drop any lingering "session expiring" toast so the two don't stack.
          this.notify.clear();
          this.notify.error('Session Expired', 'Your session has expired. Please log in again.');
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (isPlatformBrowser(this.platformId) && this.connectionListener) {
      const conn = (navigator as any).connection;
      conn?.removeEventListener('change', this.connectionListener);
    }
  }

  private checkConnectionSpeed(): void {
    const conn = (navigator as any).connection;
    if (!conn) return;
    this.isSlowNetwork.set(SLOW_TYPES.has(conn.effectiveType));
  }

  onMaskClick(): void { this.layoutService.closeOverlay(); }
  onSidebarMouseEnter(): void { this.layoutService.onSidebarMouseEnter(); }
  onSidebarMouseLeave(): void { this.layoutService.onSidebarMouseLeave(); }

  @HostListener('window:offline')
  onOffline(): void { this.isOffline.set(true); }

  @HostListener('window:online')
  onOnline(): void {
    this.isOffline.set(false);
    this.checkConnectionSpeed();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.layoutService.isMobile() && this.layoutService.state().overlayMenuActive) {
      // keep state; CSS media query handles visual adjustments
    }
  }
}
